import { useCallback, useEffect, useState } from "react";
import { useAppContext } from "./useAppContext";
import { useMessage } from "./useMessage";
import { useTextZoneDashboard } from "./useTextZoneDashboard";

const areColorsEqual = (
	color1: readonly eXpress.core.IColor[],
	color2: readonly eXpress.core.IColor[],
): boolean => {
	return eXpress.collections.arrays.equals(
		color1.map((x) => `${x.hexColorString}_${x.colorStyle || ""}`),
		color2.map((x) => `${x.hexColorString}_${x.colorStyle || ""}`),
	);
};

/**
 * The colors used by textzones in the current definition
 * Because hooks cannot be conditionally loaded, we provide optional boolean to indicate when to load
 */
export function useUsedTextForegroundColors({
	fromDefinition = true,
	fromConfiguration = false,
}: {
	fromDefinition?: boolean;
	fromConfiguration?: boolean;
} = {}): readonly eXpress.core.IColor[] {
	const { editor } = useAppContext();
	const textDashboard = useTextZoneDashboard();

	const getUsedFontFamilies = useCallback(() => {
		// Get Fonts used in Definitions
		const usedInDefinition =
			fromDefinition && editor.definition
				? textDashboard.getUsedTextForegroundColors(editor.definition)
				: [];

		// Get Fonts used in Configuration
		const usedInConfiguration =
			fromConfiguration && editor.configuration
				? textDashboard.getUsedTextForegroundColors(editor.configuration)
				: [];

		const uniqueColors = [
			...new Map(
				[...usedInConfiguration, ...usedInDefinition].map((item) => [
					item["hexColorString"],
					item,
				]),
			).values(),
		];
		return uniqueColors;
	}, [textDashboard, editor, fromConfiguration, fromDefinition]);

	// use setState to trigger a re-render
	const [usedTextForegroundColors, setUsedTextForegroundColors] = useState<
		readonly eXpress.core.IColor[]
	>([]);
	// Update if one of the arguments changes
	useEffect(() => {
		setUsedTextForegroundColors(getUsedFontFamilies());
	}, [getUsedFontFamilies]);

	const onMessage = useCallback(
		(message: eXpress.core.IMessage) => {
			// Should update?
			let shouldUpdateUsedForegroundColors = false;
			if (fromConfiguration && eXpress.core.isNewDefinitionLoaded(message))
				shouldUpdateUsedForegroundColors = true;
			if (
				!shouldUpdateUsedForegroundColors &&
				fromDefinition &&
				eXpress.core.canChangeUsedTextColors(message)
			) {
				shouldUpdateUsedForegroundColors = true;
			}

			if (shouldUpdateUsedForegroundColors) {
				const newUsedTextForegroundColors = getUsedFontFamilies();

				// Prevent rerender if array contains the same values
				if (!areColorsEqual(usedTextForegroundColors, newUsedTextForegroundColors)) {
					setUsedTextForegroundColors(newUsedTextForegroundColors);
				}
				return;
			}

			// Allow to disable it on development builds, if isDebugMode is changed at runtime, it will cause errors because of conditional hooks
			if (eXpress.isDebugMode) {
				if (process.env.PUBLIC_DEBUG === "true") {
					// This code should detect if there are messages that also change the used fonts
					// Also checked when disabled
					const newUsedTextForegroundColors = getUsedFontFamilies();
					// Remark:
					// Because useState is used, the previous value will be the value before previous reconciliation
					// Not from before this message, so this can give false results
					if (!areColorsEqual(usedTextForegroundColors, newUsedTextForegroundColors)) {
						const messageName = eXpress.core.getMessageName(message);

						throw new eXpress.UnhandledMessageException(
							`useUsedTextForegroundColors: Message '${messageName}' did update a font color but is not included.`,
						);
					}
				}
			}
		},
		[fromConfiguration, fromDefinition, getUsedFontFamilies, usedTextForegroundColors],
	);

	useMessage(onMessage);

	return usedTextForegroundColors;
}
